/**
 * A custom-styled "Sign in with Google" button driving the GIS OAuth popup
 * code flow (`google.accounts.oauth2`). We own the markup — so the button
 * matches the app's theme instead of Google's fixed FedCM chrome — while
 * Google still owns the account-chooser popup. The one-time authorization
 * code it returns goes to `POST /api/auth/google`, which exchanges it
 * server-side (the client secret never touches the browser).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { GOOGLE_OAUTH_CLIENT_ID } from '../env'
import { useI18n } from '../i18n'
import { useAuth } from './AuthContext'
import './GoogleSignInButton.css'

const GSI_SRC = 'https://accounts.google.com/gsi/client'
let scriptPromise: Promise<void> | null = null

function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = GSI_SRC
    script.dataset.gsiClient = 'true'
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

/** The official multi-color Google "G", inline so it inherits no theme color. */
function GoogleGlyph() {
  return (
    <svg className="btn-google-icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9086c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1818l-2.9086-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.964 10.71z"
      />
      <path
        fill="#EA4335"
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"
      />
    </svg>
  )
}

export function GoogleSignInButton() {
  const { signInWithGoogleCode } = useAuth()
  const { t } = useI18n()
  const [unavailable, setUnavailable] = useState(!GOOGLE_OAUTH_CLIENT_ID)
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)
  const codeClientRef = useRef<{ requestCode: () => void } | null>(null)

  useEffect(() => {
    if (!GOOGLE_OAUTH_CLIENT_ID) return
    let cancelled = false
    loadGoogleIdentityScript()
      .then(() => {
        if (cancelled || !window.google) return
        codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
          client_id: GOOGLE_OAUTH_CLIENT_ID,
          scope: 'openid email profile',
          ux_mode: 'popup',
          callback: (response) => {
            setBusy(false)
            // A dismissed popup or a denied consent yields no code — do nothing.
            if (!response.code) return
            // `AuthContext` re-throws so callers can react; this one has already
            // reacted by rendering `error` (see `SignInCta`), so swallow the
            // rejection here rather than leaving an uncaught one in the console.
            signInWithGoogleCode(response.code).catch(() => undefined)
          },
          error_callback: () => setBusy(false),
        })
        setReady(true)
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true)
      })
    return () => {
      cancelled = true
    }
  }, [signInWithGoogleCode])

  const onClick = useCallback(() => {
    if (!codeClientRef.current) return
    setBusy(true)
    codeClientRef.current.requestCode()
  }, [])

  // Say why, rather than presenting a dead button (constitution §VI). This is
  // reached when the bundle was built without `VITE_GOOGLE_OAUTH_CLIENT_ID` —
  // a build-arg mistake, not something the user can act on — or when Google's
  // script failed to load. Either way the email path below still works, and
  // silence here reads to the user as "sign-in is broken".
  if (unavailable) {
    return (
      <>
        <button type="button" className="btn btn-primary" disabled>
          {t('common.signIn')}
        </button>
        <p className="form-error btn-google-unavailable">{t('auth.googleUnavailable')}</p>
      </>
    )
  }

  return (
    <button type="button" className="btn btn-google" onClick={onClick} disabled={!ready || busy}>
      <GoogleGlyph />
      {t('common.signIn')}
    </button>
  )
}
